import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { events as eventsApi } from "../lib/api";
import { to12hr, prettyDate } from "../lib/format";
import { HERO_PHOTO } from "../lib/assets";
import { Calendar, Clock, Mic, Ticket, ChevronLeft } from "lucide-react";
import { useReservationModal } from "../contexts/ReservationModalContext";

export default function EventDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { open: openReservation } = useReservationModal();
  const [ev, setEv] = useState(null);

  useEffect(() => {
    eventsApi.get(id).then(setEv).catch(() => setEv(false));
  }, [id]);

  if (ev === null) return <div className="pt-40 text-center text-muted italic">Loading…</div>;
  if (ev === false) return <div className="pt-40 text-center text-muted italic">Event not found</div>;

  return (
    <main data-testid="event-detail-page" className="bg-white min-h-screen">
      <div className="relative h-[60vh] min-h-[420px] overflow-hidden bg-ink">
        <img
          src={ev.flyer_url || HERO_PHOTO}
          alt={ev.title}
          className="absolute inset-0 w-full h-full object-cover kenburns"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/60 to-black/90" />
        <div className="relative z-10 h-full max-w-[1500px] mx-auto px-6 md:px-10 lg:px-16 flex flex-col justify-end pb-12">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-[11px] tracking-luxe uppercase text-white/60 hover:text-[var(--onyx-gold)] mb-6"><ChevronLeft size={14} /> Back</button>
          <span className="eyebrow mb-3">{ev.category?.toUpperCase()}</span>
          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl text-white">{ev.title}</h1>
        </div>
      </div>

      <div className="max-w-[1500px] mx-auto px-6 md:px-10 lg:px-16 py-16 grid grid-cols-1 lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2">
          <p className="text-black/85 leading-relaxed text-lg">{ev.description}</p>
          {ev.performer && (
            <div className="mt-8 pt-8 border-t border-black/10">
              <span className="eyebrow flex items-center gap-2"><Mic size={12} /> Performing</span>
              <p className="font-display text-3xl mt-2 text-ink">{ev.performer}</p>
            </div>
          )}
        </div>
        <aside className="border border-black/10 p-8 h-fit bg-cream">
          <div className="space-y-5">
            <Info icon={<Calendar size={14} />} label="Date" value={prettyDate(ev.date)} />
            <Info icon={<Clock size={14} />} label="Hours" value={`${to12hr(ev.start_time)} – ${to12hr(ev.end_time)}`} />
            <Info icon={<Ticket size={14} />} label="GA Entry" value="Free before 12 AM" />
          </div>
          <div className="mt-8 space-y-3">
            <button data-testid="event-reserve-table" onClick={() => openReservation({ eventId: ev.id })} className="btn-luxe btn-luxe-gold w-full">Reserve Table</button>
            <button data-testid="event-buy-ga" onClick={() => openReservation({ eventId: ev.id })} className="btn-luxe btn-luxe-ghost w-full">Free Guestlist</button>
          </div>
        </aside>
      </div>
    </main>
  );
}

function Info({ icon, label, value }) {
  return (
    <div>
      <span className="eyebrow flex items-center gap-2">{icon} {label}</span>
      <p className="font-display text-2xl mt-1 text-ink">{value}</p>
    </div>
  );
}
